import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { endOfMonth, min } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { earliestSelectableDay, latestSelectableDay, validateCustomRange } from './customCloseout';
import { closeoutTestId } from './test-ids';

type EditCustomPeriodModalProps = {
  open: boolean;
  onClose: () => void;
  /** The row being edited — its current range identifies the period on the wire. */
  alertData: any;
  onConfirm: (newFrom: Date, newTo: Date) => void;
  confirmLoading?: boolean;
  confirmError?: string | null;
};

/**
 * BOPS-115 — move a custom period's dates. Only reachable before its first close & sign; the same
 * one-month / not-a-full-month / no-overlap rules apply to the new range as to a new period.
 */
const EditCustomPeriodModal: React.FC<EditCustomPeriodModalProps> = ({
  open,
  onClose,
  alertData,
  onConfirm,
  confirmLoading = false,
  confirmError = null,
}) => {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  //  Seed from the row each time the modal opens so a cancelled edit doesn't leak into the next one.
  useEffect(() => {
    if (!open) return;
    setFrom(alertData?.range?.startDate ? new Date(alertData.range.startDate) : null);
    setTo(alertData?.range?.endDate ? new Date(alertData.range.endDate) : null);
  }, [open, alertData]);

  //  Live, like the create bar — Save greys out with the reason underneath rather than a click
  //  that silently does nothing.
  const validationError = from && to ? validateCustomRange(from, to) : null;
  const canSave = Boolean(from && to) && !validationError && !confirmLoading;

  //  The same "today or in the past" bound as creating. A period stored with future dates before
  //  the rule existed still opens here — the picker won't offer those days back, and the validator
  //  blocks Save until the range is pulled into the past, which is the intended migration.
  const lastAllowedDay = latestSelectableDay();
  //  Matches the floor on the Years dropdown and the Custom range month picker — a period created
  //  below it could never be filtered back to afterwards.
  const firstAllowedDay = earliestSelectableDay();
  const toMaxDate = from ? min([endOfMonth(from), lastAllowedDay]) : lastAllowedDay;

  const handleConfirm = () => {
    if (!canSave || !from || !to) return;
    onConfirm(from, to);
  };

  return (
    <Dialog
      data-testid={closeoutTestId('edit-period-dialog')}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600, pb: 0 }}>
        Edit closeout period
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            sx={{ pt: 3 }}
          >
            <DatePicker
              label="From"
              value={from}
              minDate={firstAllowedDay}
              maxDate={lastAllowedDay}
              onChange={setFrom}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: Boolean(validationError),
                  inputProps: { 'data-testid': closeoutTestId('edit-period-from') },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: '#6C737F', display: { xs: 'none', sm: 'block' } }}
            >
              to
            </Typography>
            <DatePicker
              label="To"
              value={to}
              minDate={from ?? firstAllowedDay}
              maxDate={toMaxDate}
              onChange={setTo}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: Boolean(validationError),
                  inputProps: { 'data-testid': closeoutTestId('edit-period-to') },
                },
              }}
            />
          </Stack>
        </LocalizationProvider>
        <Typography
          variant="body2"
          sx={{ color: '#6C737F', mt: 2 }}
        >
          Dates must stay within one calendar month, end today or earlier, and can&apos;t overlap
          another closeout period. Changing the range refreshes this period&apos;s alerts. Editing
          is allowed only until the first close &amp; sign.
        </Typography>
        {(validationError || confirmError) && (
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon fontSize="small" />}
            sx={{
              mt: 2,
              py: 0.75,
              borderRadius: '8px',
              border: '1px solid #FDA29B',
              backgroundColor: '#FFFBFA',
              color: '#B42318',
              '& .MuiAlert-icon': { color: '#D92D20' },
              '& .MuiAlert-message': { fontSize: '0.875rem', py: 0.5 },
            }}
          >
            {validationError || confirmError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          color="inherit"
          onClick={onClose}
          disabled={confirmLoading}
          data-testid={closeoutTestId('edit-period-cancel')}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSave}
          data-testid={closeoutTestId('edit-period-save')}
          sx={{ textTransform: 'none' }}
        >
          {confirmLoading ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCustomPeriodModal;
